import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_DICTIONARIES } from 'constants/rightsCodes';
import DictionaryDetailListBrowser from './DictionaryDetailListBrowser';
import { useQuery } from 'hooks/apollo';
import { DICTIONARY_QUERY } from '../queries';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { withAreaGetter } from 'providers/AreasConsumers';
import Portal from 'utils/portal/Portal';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { useTranslation } from 'react-i18next';
import { areas } from 'constants/areas';
import FlexBox from 'components/common/FlexBox';
import PermissionCheck from 'components/common/PermissionCheck';
import { Tooltip } from '@mui/material';
import { DictionaryDetailsTitleContainer } from './Styles';

const Route = ({ getElemByAreaKey }) => {
  const { t } = useTranslation();
  const historyContext = useHistoryWithContext();
  const match = useRouteMatch();
  const id = parseInt(match.params.id);

  const canRead = usePermission(ADMIN_DICTIONARIES);

  const { data, loading } = useQuery(DICTIONARY_QUERY, {
    variables: { id },
    skip: !canRead
  });

  const handleBack = useCallback(() => {
    historyContext.push(`/dictionaries`);
  }, [historyContext]);

  const dictionary = useMemo(() => data?.dictionary || {}, [data]);

  return (
    <>
      <Portal container={getElemByAreaKey(areas.header)}>
        <Tooltip
          title={
            <FlexBox direction='column' p={0.5} gap={0.5}>
              <Typography>
                {t('Dictionaries.DictionaryItem.DictionaryName')} <strong>{dictionary.name}</strong>
              </Typography>
              <Typography>
                {t('Dictionaries.DictionaryItem.DictionaryFriendlyName')} <strong>{dictionary.friendlyName}</strong>
              </Typography>
            </FlexBox>
          }
          placement='bottom-start'
        >
          <DictionaryDetailsTitleContainer>
            <Typography noWrap variant='subtitle1' fontWeight='bold'>
              {t('Dictionaries.DictionaryItem.DictionaryName')}
              <i>{dictionary.name}</i>, {t('Dictionaries.DictionaryItem.DictionaryFriendlyName')}
              <i>{dictionary.friendlyName}</i>
            </Typography>
          </DictionaryDetailsTitleContainer>
        </Tooltip>
      </Portal>
      <Portal container={getElemByAreaKey(areas.headerLinks)}>
        <FlexBox gap={0.5} id='dictionary-details-header-actions'>
          <IconButton fontSize='medium' color='transparent' tooltip={t('BackToList')} onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        </FlexBox>
      </Portal>
      <PermissionCheck authorizationCode={ADMIN_DICTIONARIES}>
        {loading && <WrappedFakeText />}
        {!loading && <DictionaryDetailListBrowser dictionary={dictionary} />}
      </PermissionCheck>
    </>
  );
};

Route.propTypes = {
  getElemByAreaKey: PropTypes.func
};

export default withAreaGetter(Route);
